import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Search,
  Plus,
  Play,
  Pause,
  RefreshCw,
  Store,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  MoreVertical,
  Map,
  HeartPulse,
  Shield,
  ChevronDown,
  ChevronUp,
  Zap,
  Edit3,
  Save,
  X,
  Users,
  Link2,
  Copy,
  StopCircle,
  Download,
  Eye,
  FileText,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { DataTable, type SortConfig } from '../../components/admin/tables';
import { useToast } from '../../components/common/Toast';
import { api } from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

type SeedStatus = 'active' | 'paused' | 'inactive';

interface SeedRunningProgress {
  total_stores: number;
  stores_completed: number;
  current_action: string | null;
}

interface SeedCategoryStat {
  products: number;
  stores: number;
  percentage: number;
}

interface SeedLastStats {
  asis: SeedCategoryStat;
  last_chance: SeedCategoryStat;
  family_offers: SeedCategoryStat;
  limited_time: SeedCategoryStat;
  locations: number;
}

interface Seed {
  id: number;
  name: string;
  country: string;
  timezone: string;
  status: SeedStatus;
  center_latitude: number | null;
  center_longitude: number | null;
  radius_miles: number;
  auto_assign_enabled: boolean;
  last_run_at: string | null;
  last_run_status: string | null;
  store_count: number;
  running_progress: SeedRunningProgress | null;
  next_run_at: string | null;
  is_queued: boolean;
  current_store_index: number;
  last_stats: SeedLastStats | null;
  error_count_24h: number;
  schedules: Array<{
    id: number;
    run_type: string;
    cron_expression: string;
    enabled: boolean;
  }>;
}

interface SeedStore {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  country: string;
}

interface SeedScheduleDetails {
  id: number;
  seed_id: number;
  run_type: string;
  cron_expression: string;
  timezone: string;
  enabled: boolean;
  next_run_at: string | null;
  stores_completed?: number;
  total_stores?: number;
  queue_order?: number;
}

interface SeedRunSummary {
  id: number;
  store_id: string | null;
  run_type: string | null;
  status: string;
  products_found: number | null;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
}

interface SeedWithDetails extends Seed {
  assigned_stores?: SeedStore[];
  schedules: SeedScheduleDetails[];
  recent_runs?: SeedRunSummary[];
}

/**
 * Watchdog Panel - Shows schedule status and stuck schedule recovery
 * Auto-expands when issues are detected
 */
function WatchdogPanel() {
  const { t } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [wasAutoExpanded, setWasAutoExpanded] = useState(false);
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { data: scheduleStatus, isLoading, refetch } = useQuery({
    queryKey: ['admin-schedule-status'],
    queryFn: () => api.getAdminScheduleStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const watchdogMutation = useMutation({
    mutationFn: () => api.triggerAdminWatchdog(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-schedule-status'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      if (data.recovered > 0 || data.paused > 0) {
        addToast('success', `Watchdog: Recovered ${data.recovered}, Paused ${data.paused} schedules`);
      } else {
        addToast('info', 'Watchdog: No stuck schedules found');
      }
    },
    onError: (error: Error) => {
      addToast('error', `Watchdog failed: ${error.message}`);
    },
  });

  const recoverMutation = useMutation({
    mutationFn: (scheduleId: number) => api.recoverAdminSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-schedule-status'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      addToast('success', 'Schedule recovered successfully');
    },
    onError: (error: Error) => {
      addToast('error', `Recovery failed: ${error.message}`);
    },
  });

  const summary = scheduleStatus?.summary;
  const stuckSchedules = scheduleStatus?.schedules?.filter(s => s.is_stuck) || [];
  const hasIssues = (summary?.stuck || 0) > 0 || (summary?.with_errors || 0) > 0;

  // Auto-expand when issues are detected (only once per issue occurrence)
  useEffect(() => {
    if (hasIssues && !wasAutoExpanded) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Auto-expand panel when issues detected
      setIsExpanded(true);
      setWasAutoExpanded(true);
    } else if (!hasIssues && wasAutoExpanded) {
      // Reset the flag when issues are resolved
      setWasAutoExpanded(false);
    }
  }, [hasIssues, wasAutoExpanded]);

  return (
    <div className={`bg-white rounded-xl shadow-sm border ${hasIssues ? 'border-red-200' : 'border-gray-200'} ${
      hasIssues && !isExpanded ? 'animate-pulse' : ''
    }`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 rounded-xl transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${hasIssues ? 'bg-red-100' : 'bg-green-100'}`}>
            <Shield className={hasIssues ? 'text-red-600' : 'text-green-600'} size={20} />
          </div>
          <div className="text-left">
            <p className="font-medium text-gray-900">Schedule Watchdog</p>
            {summary && (
              <p className="text-sm text-gray-500">
                {summary.running} running, {summary.stuck} stuck, {summary.due} due
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasIssues && (
            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
              {summary?.stuck || 0} stuck
            </span>
          )}
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          {/* Summary Stats */}
          {summary && (
            <div className="grid grid-cols-4 gap-3 mt-4 mb-4">
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold text-gray-900">{summary.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <div className="text-lg font-semibold text-blue-600">{summary.running}</div>
                <div className="text-xs text-gray-500">Running</div>
              </div>
              <div className={`text-center p-2 rounded-lg ${summary.stuck > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                <div className={`text-lg font-semibold ${summary.stuck > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {summary.stuck}
                </div>
                <div className="text-xs text-gray-500">Stuck</div>
              </div>
              <div className={`text-center p-2 rounded-lg ${summary.with_errors > 0 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                <div className={`text-lg font-semibold ${summary.with_errors > 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                  {summary.with_errors}
                </div>
                <div className="text-xs text-gray-500">With Errors</div>
              </div>
            </div>
          )}

          {/* Stuck Schedules */}
          {stuckSchedules.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Stuck Schedules</h4>
              <div className="space-y-2">
                {stuckSchedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="flex items-center justify-between p-3 bg-red-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm text-gray-900">
                        {schedule.seed_name} - {schedule.run_type}
                      </p>
                      <RunningTime startedAt={schedule.started_at!} storesCompleted={schedule.stores_completed} totalStores={schedule.total_stores} />
                      {schedule.last_error && (
                        <p className="text-xs text-red-600 mt-1">{schedule.last_error}</p>
                      )}
                    </div>
                    <button
                      onClick={() => recoverMutation.mutate(schedule.id)}
                      disabled={recoverMutation.isPending}
                      className="px-3 py-1.5 text-xs font-medium text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Recover
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => watchdogMutation.mutate()}
              disabled={watchdogMutation.isPending}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Zap size={16} />
              {watchdogMutation.isPending ? t('admin.seedBrowsers.running', 'Running...') : t('admin.seedBrowsers.runWatchdog', 'Run Watchdog')}
            </button>
            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Schedule Edit Row - Editable schedule item
 */
interface ScheduleEditRowProps {
  schedule: {
    id: number;
    run_type: string;
    cron_expression: string;
    enabled: boolean;
    next_run_at?: string | null;
    consecutive_failures?: number;
    last_error_message?: string | null;
  };
  seedId: number;
  onUpdate: () => void;
}

function ScheduleEditRow({ schedule, seedId, onUpdate }: ScheduleEditRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [cronExpression, setCronExpression] = useState(schedule.cron_expression);
  const { addToast } = useToast();

  const updateMutation = useMutation({
    mutationFn: (data: { cron_expression?: string; enabled?: boolean }) =>
      api.updateAdminSeedSchedule(seedId, schedule.id, data),
    onSuccess: () => {
      setIsEditing(false);
      onUpdate();
      addToast('success', 'Schedule updated');
    },
    onError: (error: Error) => {
      addToast('error', `Failed to update: ${error.message}`);
    },
  });

  const handleToggleEnabled = () => {
    updateMutation.mutate({ enabled: !schedule.enabled });
  };

  const handleSaveCron = () => {
    if (cronExpression.trim() !== schedule.cron_expression) {
      updateMutation.mutate({ cron_expression: cronExpression.trim() });
    } else {
      setIsEditing(false);
    }
  };

  const hasErrors = (schedule.consecutive_failures || 0) > 0;

  return (
    <div className={`p-3 rounded-lg ${hasErrors ? 'bg-red-50 border border-red-100' : 'bg-gray-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">
              {schedule.run_type === 'combined' ? 'Daily Combined Run' : schedule.run_type}
            </p>
            {hasErrors && (
              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-600 rounded">
                {schedule.consecutive_failures} failures
              </span>
            )}
          </div>
          {isEditing ? (
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                value={cronExpression}
                onChange={(e) => setCronExpression(e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0 10 * * *"
              />
              <button
                onClick={handleSaveCron}
                disabled={updateMutation.isPending}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
              >
                <Save size={14} />
              </button>
              <button
                onClick={() => {
                  setCronExpression(schedule.cron_expression);
                  setIsEditing(false);
                }}
                className="p-1 text-gray-400 hover:bg-gray-100 rounded"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-gray-500 font-mono">{schedule.cron_expression}</p>
              <button
                onClick={() => setIsEditing(true)}
                className="p-0.5 text-gray-400 hover:text-gray-600"
              >
                <Edit3 size={12} />
              </button>
            </div>
          )}
          {schedule.next_run_at && (
            <p className="text-xs text-gray-400 mt-1">
              Next: {new Date(schedule.next_run_at).toLocaleString()}
            </p>
          )}
          {schedule.last_error_message && (
            <p className="text-xs text-red-500 mt-1 truncate" title={schedule.last_error_message}>
              {schedule.last_error_message}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleToggleEnabled}
            disabled={updateMutation.isPending}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              schedule.enabled ? 'bg-green-500' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                schedule.enabled ? 'translate-x-5' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * RunStateBadge - Shows current execution state: Idle, Queued, Running
 */
function RunStateBadge({
  seed
}: {
  seed: Seed;
}) {
  const isDisabled = seed.status !== 'active';
  const isRunning = seed.running_progress !== null || seed.current_store_index > 0;
  const isQueued = seed.is_queued && !isRunning;

  if (isDisabled) {
    return <span className="text-gray-400">-</span>;
  }

  if (isRunning) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </span>
        Running
      </span>
    );
  }

  if (isQueued) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
        <Clock size={12} />
        Queued
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
      Idle
    </span>
  );
}

/**
 * ProgressCell - Shows progress bar with store count and current store name when running
 */
function ProgressCell({
  seed
}: {
  seed: Seed;
}) {
  const isRunning = seed.running_progress !== null || seed.current_store_index > 0;

  if (!isRunning) {
    return <span className="text-gray-400">-</span>;
  }

  const completed = seed.running_progress?.stores_completed ?? seed.current_store_index;
  const total = seed.running_progress?.total_stores ?? seed.store_count;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const currentAction = seed.running_progress?.current_action;

  // Extract store name from current_action if it contains one
  // Format is typically "Processing StoreName" or just "StoreName"
  const storeName = currentAction?.replace(/^Processing\s+/i, '') || `Store ${completed + 1}`;

  return (
    <div className="flex flex-col gap-1 min-w-[160px]">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-xs font-medium text-gray-600 w-12">{completed}/{total}</span>
      </div>
      <span className="text-xs text-gray-500 truncate" title={storeName}>
        {storeName}
      </span>
    </div>
  );
}

/**
 * LastCompletedCell - Shows when last run completed with success/fail indicator
 */
function LastCompletedCell({
  seed
}: {
  seed: Seed;
}) {
  if (!seed.last_run_at) {
    return <span className="text-gray-400">Never</span>;
  }

  const isSuccess = seed.last_run_status === 'success';
  const isFailed = seed.last_run_status === 'error';
  const timeAgo = getTimeAgo(seed.last_run_at);

  return (
    <a
      href={`/admin/seeds/${seed.id}/health`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1.5 text-sm hover:underline"
      title="View health report"
    >
      {isSuccess ? (
        <CheckCircle size={14} className="text-green-500" />
      ) : isFailed ? (
        <AlertCircle size={14} className="text-red-500" />
      ) : (
        <Clock size={14} className="text-yellow-500" />
      )}
      <span className="text-gray-600">{timeAgo}</span>
    </a>
  );
}

/**
 * NextRunCell - Shows countdown to next scheduled run
 */
function NextRunCell({
  seed
}: {
  seed: Seed;
}) {
  const isDisabled = seed.status !== 'active';
  const isRunning = seed.running_progress !== null || seed.current_store_index > 0;

  if (isDisabled || !seed.next_run_at || isRunning) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <span className="text-sm text-gray-600">
      {formatNextRun(seed.next_run_at)}
    </span>
  );
}

/**
 * Format store coverage stat as "X/Y" with product count tooltip
 */
function formatStoreStat(stat: SeedCategoryStat, label: string): React.ReactNode {
  if (stat.products === 0 && stat.stores === 0) return null;

  const isFullCoverage = stat.percentage === 100;

  return (
    <div
      className="flex items-center gap-1"
      title={`${stat.products} products from ${stat.stores} stores (${stat.percentage}%)`}
    >
      <span className={isFullCoverage ? 'text-green-600 font-medium' : 'text-gray-600'}>
        {stat.stores} ({stat.percentage}%)
      </span>
      <span className="text-gray-400">{label}</span>
    </div>
  );
}

/**
 * LastStatsCell - Shows store coverage from last completed run
 */
function LastStatsCell({ seed }: { seed: Seed }) {
  const stats = seed.last_stats;

  if (!stats) {
    return <span className="text-gray-400 text-xs">No data</span>;
  }

  // Check if any category has data
  const hasAnyData =
    stats.asis?.products > 0 ||
    stats.last_chance?.products > 0 ||
    stats.family_offers?.products > 0 ||
    stats.limited_time?.products > 0 ||
    stats.locations > 0;

  if (!hasAnyData) {
    return <span className="text-gray-400 text-xs">No data</span>;
  }

  return (
    <div className="text-xs space-y-0.5">
      {formatStoreStat(stats.asis, 'As-Is')}
      {formatStoreStat(stats.last_chance, 'Last Chance')}
      {formatStoreStat(stats.family_offers, 'Family')}
      {formatStoreStat(stats.limited_time, 'Limited')}
      {stats.locations > 0 && (
        <div className="text-gray-500">{stats.locations} Locations</div>
      )}
    </div>
  );
}

/**
 * ErrorCountCell - Shows error count in last 24h with color-coded badge
 */
function ErrorCountCell({ seed }: { seed: Seed }) {
  const errorCount = seed.error_count_24h || 0;

  if (errorCount === 0) {
    return (
      <span className="inline-flex items-center text-green-600" title="No errors in last 24h">
        <CheckCircle size={16} />
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700"
      title={`${errorCount} error${errorCount !== 1 ? 's' : ''} in last 24h`}
    >
      <AlertCircle size={12} />
      {errorCount}
    </span>
  );
}

/**
 * StatusBadge - Shows seed status (used in details drawer)
 */
function StatusBadge({ status }: { status: SeedStatus }) {
  const styles = {
    active: 'bg-green-100 text-green-700',
    paused: 'bg-yellow-100 text-yellow-700',
    inactive: 'bg-gray-100 text-gray-500',
  };

  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${styles[status]}`}>
      {status}
    </span>
  );
}

// Helper functions moved above to avoid reference errors

function formatNextRun(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  // If in the past, show "Due"
  if (diff < 0) {
    return 'Due now';
  }

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `in ${minutes}m`;
  if (hours < 24) {
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `in ${hours}h`;
    return `in ${hours}h ${remainingMinutes}m`;
  }
  if (days === 1) {
    return `Tomorrow ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  return date.toLocaleDateString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) {
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours}h ago`;
    return `${hours}h ${remainingMinutes}m ago`;
  }
  return `${days}d ago`;
}

/**
 * Get country flag image URL from ISO country code
 * Uses flagcdn.com for consistent cross-platform display
 */
function getCountryFlagUrl(countryCode: string): string {
  return `https://flagcdn.com/16x12/${countryCode.toLowerCase()}.png`;
}

/**
 * Component to display running time - uses useEffect to update periodically
 * to avoid calling Date.now() during render
 */
function RunningTime({ startedAt, storesCompleted, totalStores }: {
  startedAt: string;
  storesCompleted?: number | null;
  totalStores?: number | null;
}) {
  const [runningMinutes, setRunningMinutes] = useState(() =>
    Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000)
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setRunningMinutes(Math.floor((Date.now() - new Date(startedAt).getTime()) / 60000));
    }, 60000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return (
    <p className="text-xs text-gray-500">
      Running for {runningMinutes} min
      {storesCompleted && totalStores && (
        <> | {storesCompleted}/{totalStores} stores</>
      )}
    </p>
  );
}

export default function SeedBrowsers() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState('');
  const [selectedSeed, setSelectedSeed] = useState<Seed | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editSeed, setEditSeed] = useState<Seed | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const navigate = useNavigate();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  // Fetch seeds - auto-refresh every 3 seconds when seeds are running or queued
  const { data: seedsData, isLoading } = useQuery({
    queryKey: ['admin-seeds', page, pageSize, search, country, status, sortConfig?.key, sortConfig?.direction],
    queryFn: () => api.getAdminSeeds({
      page,
      limit: pageSize,
      search,
      country,
      status,
      sort: sortConfig?.key,
      sortDirection: sortConfig?.direction,
    }),
    refetchInterval: (query) => {
      // Auto-refresh if any seeds are running, queued, or in progress
      const seeds = query.state.data?.seeds as Seed[] | undefined;
      const hasActive = seeds?.some(s =>
        s.running_progress !== null || s.is_queued || s.current_store_index > 0
      );
      return hasActive ? 3000 : false;
    },
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['admin-seed-stats'],
    queryFn: () => api.getAdminSeedStats(),
  });

  // Mutations
  const pauseMutation = useMutation({
    mutationFn: (seedId: number) => api.pauseAdminSeed(seedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seed-stats'] });
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (seedId: number) => api.resumeAdminSeed(seedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      queryClient.invalidateQueries({ queryKey: ['admin-seed-stats'] });
    },
  });

  const triggerRunMutation = useMutation({
    mutationFn: (seedId: number) => api.triggerAdminSeedRun(seedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      addToast('success', 'Seed run queued');
    },
    onError: (error: Error) => {
      addToast('error', `Failed to trigger run: ${error.message}`);
    },
  });

  const forceStopMutation = useMutation({
    mutationFn: (seedId: number) => api.forceStopAdminSeed(seedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
      addToast('success', 'Seed stopped');
    },
    onError: (error: Error) => {
      addToast('error', `Failed to stop seed: ${error.message}`);
    },
  });

  const handlePauseResume = (seed: Seed) => {
    if (seed.status === 'active') {
      pauseMutation.mutate(seed.id);
    } else if (seed.status === 'paused') {
      resumeMutation.mutate(seed.id);
    }
    setActionMenuOpen(null);
  };

  const handleRunNow = (seedId: number) => {
    triggerRunMutation.mutate(seedId);
    setActionMenuOpen(null);
  };

  const handleForceStop = (seedId: number) => {
    forceStopMutation.mutate(seedId);
    setActionMenuOpen(null);
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        // Toggle direction if same column
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      // New column, default to ascending
      return { key, direction: 'asc' };
    });
    // Reset to first page when sorting changes
    setPage(1);
  };

  const handleViewAll = () => {
    setPageSize(1000); // Large number to get all
    setPage(1);
  };

  const handlePagedView = () => {
    setPageSize(20);
    setPage(1);
  };

  const columns = [
    {
      key: 'name',
      header: 'Seed Name',
      sortable: true,
      render: (seed: Seed) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{seed.name}</span>
          <span className="text-xs text-gray-500">{seed.timezone}</span>
        </div>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      sortable: true,
      render: (seed: Seed) => (
        <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
          <img
            src={getCountryFlagUrl(seed.country)}
            alt={seed.country}
            className="w-4 h-3 object-cover rounded-sm"
          />
          {seed.country}
        </span>
      ),
    },
    {
      key: 'stores',
      header: 'Stores',
      sortable: true,
      sortKey: 'store_count',
      render: (seed: Seed) => (
        <div className="flex items-center gap-1 text-gray-600">
          <Store size={14} />
          <span>{seed.store_count}</span>
        </div>
      ),
    },
    {
      key: 'last_stats',
      header: 'Last Stats',
      render: (seed: Seed) => <LastStatsCell seed={seed} />,
    },
    {
      key: 'errors',
      header: 'Errors',
      sortable: true,
      sortKey: 'error_count_24h',
      render: (seed: Seed) => <ErrorCountCell seed={seed} />,
    },
    {
      key: 'run_state',
      header: 'Run State',
      sortable: true,
      sortKey: 'status',
      render: (seed: Seed) => <RunStateBadge seed={seed} />,
    },
    {
      key: 'progress',
      header: 'Progress',
      render: (seed: Seed) => <ProgressCell seed={seed} />,
    },
    {
      key: 'last_completed',
      header: 'Last Completed',
      sortable: true,
      sortKey: 'last_run_at',
      render: (seed: Seed) => <LastCompletedCell seed={seed} />,
    },
    {
      key: 'next_run',
      header: 'Next Run',
      sortable: true,
      sortKey: 'next_run_at',
      render: (seed: Seed) => <NextRunCell seed={seed} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (seed: Seed) => {
        const isRunning = seed.running_progress !== null || seed.current_store_index > 0;
        const isQueued = seed.is_queued && !isRunning;
        const canRun = seed.status === 'active' && !isRunning && !isQueued;
        const canStop = isRunning || isQueued;

        return (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActionMenuOpen(actionMenuOpen === seed.id ? null : seed.id);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <MoreVertical size={16} />
            </button>

            {actionMenuOpen === seed.id && (
              <div
                className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="menu"
                tabIndex={0}
              >
                {/* Enable/Disable Toggle */}
                {seed.status === 'active' ? (
                  <button
                    onClick={() => {
                      handlePauseResume(seed);
                      setActionMenuOpen(null);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-orange-600"
                  >
                    <Pause size={14} /> Pause Seed
                  </button>
                ) : seed.status === 'paused' ? (
                  <button
                    onClick={() => {
                      handlePauseResume(seed);
                      setActionMenuOpen(null);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                  >
                    <Play size={14} /> Resume Seed
                  </button>
                ) : null}
                {seed.status !== 'inactive' && <hr className="my-1" />}
                {canRun && (
                  <button
                    onClick={() => handleRunNow(seed.id)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-green-600"
                  >
                    <Play size={14} /> Run Now
                  </button>
                )}
                {canStop && (
                  <button
                    onClick={() => handleForceStop(seed.id)}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                  >
                    <StopCircle size={14} /> Force Stop
                  </button>
                )}
                {(canRun || canStop) && <hr className="my-1" />}
                <button
                  onClick={() => {
                    navigate(`/admin/seeds/${seed.id}/health`);
                    setActionMenuOpen(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <HeartPulse size={14} /> Health Report
                </button>
                <button
                  onClick={() => {
                    setEditSeed(seed);
                    setActionMenuOpen(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Edit3 size={14} /> Edit Seed
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Activity className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Seeds</p>
                <p className="text-2xl font-semibold">{statsData.total_seeds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Seeds</p>
                <p className="text-2xl font-semibold">{statsData.active_seeds}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Store className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Stores Covered</p>
                <p className="text-2xl font-semibold">
                  {statsData.covered_stores}/{statsData.total_stores}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Runs Today</p>
                <p className="text-2xl font-semibold">{statsData.runs_today}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Watchdog Panel */}
      <WatchdogPanel />

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search seeds..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Country Filter */}
          <select
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Countries</option>
            <optgroup label="North America">
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="MX">Mexico</option>
            </optgroup>
            <optgroup label="Europe">
              <option value="DE">Germany</option>
              <option value="GB">United Kingdom</option>
              <option value="FR">France</option>
              <option value="SE">Sweden</option>
              <option value="NO">Norway</option>
              <option value="DK">Denmark</option>
              <option value="FI">Finland</option>
              <option value="NL">Netherlands</option>
              <option value="BE">Belgium</option>
              <option value="AT">Austria</option>
              <option value="CH">Switzerland</option>
              <option value="ES">Spain</option>
              <option value="IT">Italy</option>
              <option value="PL">Poland</option>
              <option value="IE">Ireland</option>
              <option value="PT">Portugal</option>
              <option value="CZ">Czech Republic</option>
              <option value="HU">Hungary</option>
              <option value="RO">Romania</option>
              <option value="SK">Slovakia</option>
              <option value="GR">Greece</option>
              <option value="SI">Slovenia</option>
              <option value="HR">Croatia</option>
              <option value="RS">Serbia</option>
            </optgroup>
            <optgroup label="Asia Pacific">
              <option value="JP">Japan</option>
              <option value="KR">South Korea</option>
              <option value="CN">China</option>
              <option value="TW">Taiwan</option>
              <option value="HK">Hong Kong</option>
              <option value="SG">Singapore</option>
              <option value="MY">Malaysia</option>
              <option value="TH">Thailand</option>
              <option value="ID">Indonesia</option>
              <option value="PH">Philippines</option>
              <option value="IN">India</option>
              <option value="AU">Australia</option>
            </optgroup>
            <optgroup label="Middle East">
              <option value="AE">UAE</option>
              <option value="SA">Saudi Arabia</option>
              <option value="QA">Qatar</option>
              <option value="KW">Kuwait</option>
              <option value="BH">Bahrain</option>
              <option value="JO">Jordan</option>
              <option value="IL">Israel</option>
            </optgroup>
            <optgroup label="Other">
              <option value="EG">Egypt</option>
              <option value="MA">Morocco</option>
              <option value="DO">Dominican Republic</option>
              <option value="PR">Puerto Rico</option>
              <option value="CL">Chile</option>
            </optgroup>
          </select>

          {/* Status Filter */}
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          <Link
            to="/admin/seeds/map"
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Map size={18} />
            View Map
          </Link>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={18} />
            Create Seed
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {seedsData && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {seedsData.seeds.length} of {seedsData.total} seeds
            {statsData && statsData.uncovered_stores > 0 && (
              <span className="text-amber-600 ml-2">
                ({statsData.uncovered_stores} stores without coverage)
              </span>
            )}
          </p>
          {pageSize === 20 ? (
            <button
              onClick={handleViewAll}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View All
            </button>
          ) : (
            <button
              onClick={handlePagedView}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Show Paginated
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <DataTable<Seed>
        columns={columns}
        data={seedsData?.seeds || []}
        loading={isLoading}
        page={page}
        totalPages={seedsData?.totalPages || 1}
        onPageChange={setPage}
        onRowClick={(seed) => setSelectedSeed(seed)}
        sortConfig={sortConfig}
        onSort={handleSort}
      />

      {/* Seed Details Drawer */}
      {selectedSeed && (
        <SeedDetailsDrawer
          seed={selectedSeed}
          onClose={() => setSelectedSeed(null)}
        />
      )}

      {/* Create Seed Modal */}
      {showCreateModal && (
        <CreateSeedModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
            queryClient.invalidateQueries({ queryKey: ['admin-seed-stats'] });
          }}
        />
      )}

      {/* Edit Seed Modal */}
      {editSeed && (
        <EditSeedModal
          seed={editSeed}
          onClose={() => setEditSeed(null)}
          onUpdated={() => {
            setEditSeed(null);
            queryClient.invalidateQueries({ queryKey: ['admin-seeds'] });
          }}
        />
      )}

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActionMenuOpen(null)}
          onKeyDown={(e) => e.key === 'Escape' && setActionMenuOpen(null)}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}
    </div>
  );
}

// Seed Referral Stats Component
function SeedReferralStats({ seedId }: { seedId: number }) {
  const { addToast } = useToast();
  const { data: referralData, isLoading } = useQuery({
    queryKey: ['admin-seed-referrals', seedId],
    queryFn: () => api.getAdminSeedReferrals(seedId),
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    addToast('success', 'Copied to clipboard!');
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading referral data...</div>;
  }

  if (!referralData) {
    return null;
  }

  if (!referralData.has_user_account) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <Users className="mx-auto text-gray-400 mb-2" size={24} />
        <p className="text-sm text-gray-500">No user account yet</p>
        <p className="text-xs text-gray-400 mt-1">
          Account will be created when seed first authenticates
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Referral Code & Link */}
      <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Referral Code</span>
          <button
            onClick={() => referralData.referral_code && copyToClipboard(referralData.referral_code)}
            className="p-1 hover:bg-white/50 rounded"
            title="Copy code"
          >
            <Copy size={14} className="text-gray-500" />
          </button>
        </div>
        <p className="font-mono text-lg font-bold text-primary-700">
          {referralData.referral_code || 'N/A'}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <Link2 size={12} className="text-gray-400" />
          <a
            href={referralData.referral_link || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline truncate"
          >
            {referralData.referral_link || 'N/A'}
          </a>
          <button
            onClick={() => referralData.referral_link && copyToClipboard(referralData.referral_link)}
            className="p-0.5 hover:bg-white/50 rounded flex-shrink-0"
            title="Copy link"
          >
            <Copy size={12} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Referral Stats Grid */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-gray-900">
            {referralData.total_referrals}
          </p>
          <p className="text-xs text-gray-500">Total Referrals</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-green-600">
            {referralData.successful_referrals}
          </p>
          <p className="text-xs text-gray-500">Successful</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">
            {referralData.conversion_rate}%
          </p>
          <p className="text-xs text-gray-500">Conversion</p>
        </div>
      </div>

      {/* Time Period Breakdown */}
      {referralData.referrals_by_period && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-gray-50 rounded p-2">
            <p className="text-sm font-medium">{referralData.referrals_by_period.last_24h}</p>
            <p className="text-xs text-gray-400">24h</p>
          </div>
          <div className="bg-gray-50 rounded p-2">
            <p className="text-sm font-medium">{referralData.referrals_by_period.last_7d}</p>
            <p className="text-xs text-gray-400">7d</p>
          </div>
          <div className="bg-gray-50 rounded p-2">
            <p className="text-sm font-medium">{referralData.referrals_by_period.last_30d}</p>
            <p className="text-xs text-gray-400">30d</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Phase badge color helper
const phaseBadgeStyles: Record<string, string> = {
  auth: 'bg-purple-100 text-purple-700',
  browser_launch: 'bg-red-100 text-red-700',
  navigation: 'bg-blue-100 text-blue-700',
  wait_for_content: 'bg-blue-100 text-blue-700',
  show_more: 'bg-blue-100 text-blue-700',
  overlay_processing: 'bg-blue-100 text-blue-700',
  parsing: 'bg-green-100 text-green-700',
  contribution: 'bg-orange-100 text-orange-700',
  db_update: 'bg-orange-100 text-orange-700',
  coordination: 'bg-gray-100 text-gray-700',
  unknown: 'bg-gray-100 text-gray-500',
};

// Error Details Section Component
function ErrorDetailsSection({ seedId }: { seedId: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedErrors, setExpandedErrors] = useState<Set<number>>(new Set());

  const { data: errorData, isLoading } = useQuery({
    queryKey: ['seed-errors', seedId],
    queryFn: () => api.getAdminSeedErrors(seedId, 24),
    enabled: isExpanded,
  });

  const hasErrors = (errorData?.total_errors || 0) > 0;

  const toggleErrorExpand = (errorId: number) => {
    const newSet = new Set(expandedErrors);
    if (newSet.has(errorId)) {
      newSet.delete(errorId);
    } else {
      newSet.add(errorId);
    }
    setExpandedErrors(newSet);
  };

  // Group errors by error name for summary
  const errorSummary = errorData?.errors?.reduce((acc, err) => {
    const key = err.error_name;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  // Export errors to CSV
  const exportToCSV = () => {
    if (!errorData?.errors?.length) return;

    const headers = ['Time', 'Store', 'Phase', 'Error', 'Message', 'URL'];
    const rows = errorData.errors.map(e => [
      new Date(e.created_at).toISOString(),
      e.store_id || '',
      e.error_phase,
      e.error_name,
      e.error_message,
      e.page_url || '',
    ]);

    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seed-${seedId}-errors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <AlertCircle size={18} className={hasErrors ? 'text-red-500' : 'text-gray-400'} />
          <h3 className="font-medium">Errors (24h)</h3>
          {isExpanded && hasErrors && (
            <span className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
              {errorData?.total_errors}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isExpanded && hasErrors && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                exportToCSV();
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Export errors to CSV"
            >
              <Download size={16} className="text-gray-500" />
            </button>
          )}
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading errors...</div>
          ) : !hasErrors ? (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <CheckCircle size={16} className="text-green-500" />
              No errors in the last 24 hours
            </div>
          ) : (
            <>
              {/* Error Summary */}
              <div className="flex flex-wrap gap-2">
                {Object.entries(errorSummary).map(([name, count]) => (
                  <span
                    key={name}
                    className="px-2 py-1 text-xs bg-gray-100 rounded-full"
                    title={name}
                  >
                    {name.length > 20 ? name.substring(0, 20) + '...' : name}: {count}
                  </span>
                ))}
              </div>

              {/* Error List */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {errorData?.errors?.slice(0, 20).map((error) => (
                  <button
                    type="button"
                    key={error.id}
                    className="w-full text-left p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleErrorExpand(error.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate">
                            {error.error_name}
                          </span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${phaseBadgeStyles[error.error_phase] || phaseBadgeStyles.unknown}`}>
                            {error.error_phase}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {error.store_id && <span className="mr-2">{error.store_id}</span>}
                          {getTimeAgo(error.created_at)}
                        </div>
                      </div>
                    </div>
                    {expandedErrors.has(error.id) && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-700 break-words">
                          {error.error_message}
                        </p>
                        {error.page_url && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            URL: {error.page_url}
                          </p>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {errorData && errorData.errors.length > 20 && (
                <p className="text-xs text-gray-400 text-center">
                  Showing 20 of {errorData.total_errors} errors
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Timing Bar Component - Visual breakdown of timing phases
function TimingBar({
  browserLaunch,
  navigation,
  parsing,
  contribution,
  total,
}: {
  browserLaunch: number;
  navigation: number;
  parsing: number;
  contribution: number;
  total: number;
}) {
  if (total === 0) return null;

  const getPercent = (val: number) => Math.max(0, Math.min(100, (val / total) * 100));
  const formatMs = (ms: number) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden flex">
        {browserLaunch > 0 && (
          <div
            className="bg-red-400 h-full"
            style={{ width: `${getPercent(browserLaunch)}%` }}
            title={`Browser: ${formatMs(browserLaunch)}`}
          />
        )}
        {navigation > 0 && (
          <div
            className="bg-blue-400 h-full"
            style={{ width: `${getPercent(navigation)}%` }}
            title={`Navigation: ${formatMs(navigation)}`}
          />
        )}
        {parsing > 0 && (
          <div
            className="bg-green-400 h-full"
            style={{ width: `${getPercent(parsing)}%` }}
            title={`Parsing: ${formatMs(parsing)}`}
          />
        )}
        {contribution > 0 && (
          <div
            className="bg-orange-400 h-full"
            style={{ width: `${getPercent(contribution)}%` }}
            title={`Contribution: ${formatMs(contribution)}`}
          />
        )}
      </div>
      <span className="text-xs text-gray-500 w-12 text-right">{formatMs(total)}</span>
    </div>
  );
}

// Timing Trends Chart Component
function TimingTrendsChart({ seedId }: { seedId: number }) {
  const { data: trendsData, isLoading } = useQuery({
    queryKey: ['seed-timing-trends', seedId],
    queryFn: () => api.getAdminSeedTimingTrends(seedId, 7),
  });

  if (isLoading) {
    return <div className="text-sm text-gray-500 text-center py-4">Loading trends...</div>;
  }

  if (!trendsData?.trends?.length) {
    return <div className="text-sm text-gray-500 text-center py-4">No trend data available</div>;
  }

  const runTypeColors: Record<string, string> = {
    asis: '#3B82F6',
    combined: '#10B981',
    last_chance: '#F59E0B',
    family_offers: '#8B5CF6',
    limited_time: '#EC4899',
  };

  // Get all run types across all days
  const allRunTypes = new Set<string>();
  trendsData.trends.forEach(day => {
    Object.keys(day.by_run_type || {}).forEach(type => allRunTypes.add(type));
  });

  // Prepare data with all run types
  const chartDataWithTypes = trendsData.trends.map(day => {
    const entry: Record<string, unknown> = {
      date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    };
    allRunTypes.forEach(type => {
      const typeData = day.by_run_type?.[type];
      entry[type] = typeData ? Math.round(typeData.avg_duration_ms / 1000) : null;
    });
    return entry;
  });

  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartDataWithTypes}>
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickLine={false}
            tickFormatter={(v) => `${v}s`}
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            formatter={(value: number) => [`${value}s`, '']}
          />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          {Array.from(allRunTypes).map(type => (
            <Line
              key={type}
              type="monotone"
              dataKey={type}
              name={type.replace('_', ' ')}
              stroke={runTypeColors[type] || '#6B7280'}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Timing Analysis Section Component
function TimingAnalysisSection({ seedId }: { seedId: number }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTrends, setShowTrends] = useState(false);

  const { data: timingData, isLoading } = useQuery({
    queryKey: ['seed-timing', seedId],
    queryFn: () => api.getAdminSeedTiming(seedId, 7),
    enabled: isExpanded,
  });

  const formatMs = (ms: number) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  // Calculate overall average
  const overallAvg = timingData?.stats?.by_run_type
    ? Object.values(timingData.stats.by_run_type).reduce((sum, rt) => sum + rt.avg_duration_ms * rt.count, 0) /
      Math.max(1, Object.values(timingData.stats.by_run_type).reduce((sum, rt) => sum + rt.count, 0))
    : 0;

  return (
    <div className="border-t border-gray-100 pt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-gray-400" />
          <h3 className="font-medium">Timing (7d)</h3>
          {isExpanded && timingData && (
            <span className="text-xs text-gray-500">
              avg {formatMs(overallAvg)}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-4">
          {isLoading ? (
            <div className="text-sm text-gray-500">Loading timing data...</div>
          ) : !timingData?.stats?.total_runs ? (
            <div className="text-sm text-gray-500">No timing data available</div>
          ) : (
            <>
              {/* View Tabs */}
              <div className="flex gap-2 border-b border-gray-100 pb-2">
                <button
                  onClick={() => setShowTrends(false)}
                  className={`px-3 py-1 text-xs rounded-t ${!showTrends ? 'bg-gray-100 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Stats
                </button>
                <button
                  onClick={() => setShowTrends(true)}
                  className={`px-3 py-1 text-xs rounded-t ${showTrends ? 'bg-gray-100 font-medium' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                  Trends
                </button>
              </div>

              {showTrends ? (
                <TimingTrendsChart seedId={seedId} />
              ) : (
                <>
                  {/* Timing Legend */}
                  <div className="flex flex-wrap gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-red-400 rounded" />
                      <span className="text-gray-500">Browser</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-blue-400 rounded" />
                      <span className="text-gray-500">Navigation</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-green-400 rounded" />
                      <span className="text-gray-500">Parsing</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 bg-orange-400 rounded" />
                      <span className="text-gray-500">Contribution</span>
                    </div>
                  </div>

                  {/* By Run Type */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 uppercase">By Run Type</h4>
                    {Object.entries(timingData.stats.by_run_type).map(([type, data]) => (
                      <div key={type} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="capitalize font-medium">{type.replace('_', ' ')}</span>
                          <span className="text-xs text-gray-500">{data.count} runs</span>
                        </div>
                        <TimingBar
                          browserLaunch={data.avg_browser_launch_ms}
                          navigation={data.avg_navigation_ms}
                          parsing={data.avg_parsing_ms}
                          contribution={data.avg_contribution_ms}
                          total={data.avg_duration_ms}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Slowest Runs */}
                  {timingData.stats.slowest_runs?.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-medium text-gray-500 uppercase">Slowest Runs</h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {timingData.stats.slowest_runs.slice(0, 5).map((run, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded"
                          >
                            <div>
                              <span className="font-medium">{run.store_id}</span>
                              <span className="text-gray-400 ml-2 capitalize">{run.run_type}</span>
                            </div>
                            <span className="text-red-600 font-medium">{formatMs(run.duration_ms)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Log Level Badge Styles
const logLevelStyles: Record<string, string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-700',
  warn: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
};

// Run Logs Modal Component
function RunLogsModal({
  seedId,
  runId,
  onClose,
}: {
  seedId: number;
  runId: number;
  onClose: () => void;
}) {
  const [levelFilter, setLevelFilter] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['seed-run-logs', seedId, runId],
    queryFn: () => api.getAdminSeedRunLogs(seedId, runId),
  });

  const formatMs = (ms: number) => {
    if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`;
    if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.round(ms)}ms`;
  };

  const filteredLogs = data?.logs?.filter(log =>
    !levelFilter || log.log_level === levelFilter
  ) || [];

  const logCounts = data?.logs?.reduce((acc, log) => {
    acc[log.log_level] = (acc[log.log_level] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] mx-4 flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText size={20} className="text-gray-400" />
              <div>
                <h2 className="text-lg font-semibold">Run Logs</h2>
                {data?.run && (
                  <p className="text-sm text-gray-500">
                    {data.run.store_id} · <span className="capitalize">{data.run.run_type}</span>
                    {data.run.duration_ms && ` · ${formatMs(data.run.duration_ms)}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data?.run && (
                <span className={`px-2 py-1 text-xs rounded-full ${
                  data.run.status === 'success'
                    ? 'bg-green-100 text-green-700'
                    : data.run.status === 'error'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {data.run.status}
                </span>
              )}
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading logs...</div>
          ) : (
            <div className="space-y-4">
              {/* Level Filter Tabs */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setLevelFilter(null)}
                  className={`px-3 py-1 text-xs rounded-full ${
                    !levelFilter ? 'bg-gray-900 text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  All ({data?.logs?.length || 0})
                </button>
                {Object.entries(logCounts).map(([level, count]) => (
                  <button
                    key={level}
                    onClick={() => setLevelFilter(level === levelFilter ? null : level)}
                    className={`px-3 py-1 text-xs rounded-full ${
                      levelFilter === level ? 'bg-gray-900 text-white' : logLevelStyles[level] || 'bg-gray-100'
                    }`}
                  >
                    {level} ({count})
                  </button>
                ))}
              </div>

              {/* Log Entries */}
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {filteredLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`p-3 rounded-lg text-sm ${
                      log.timing_operation ? 'bg-purple-50 border border-purple-100' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-xs rounded ${logLevelStyles[log.log_level] || 'bg-gray-100'}`}>
                          {log.log_level}
                        </span>
                        <span className="text-gray-700">{log.message}</span>
                        {log.timing_operation && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                            {log.timing_operation}: {formatMs(log.timing_duration_ms || 0)}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    {log.context && Object.keys(log.context).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                          Context
                        </summary>
                        <pre className="mt-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                          {JSON.stringify(log.context, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))}
                {filteredLogs.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No logs found
                  </div>
                )}
              </div>

              {/* Errors Section */}
              {data?.errors && data.errors.length > 0 && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="font-medium text-red-600 flex items-center gap-2 mb-3">
                    <AlertCircle size={16} />
                    Errors ({data.errors.length})
                  </h3>
                  <div className="space-y-2">
                    {data.errors.map((error) => (
                      <div
                        key={error.id}
                        className="p-3 bg-red-50 rounded-lg border border-red-100"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm text-red-700">{error.error_name}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded ${phaseBadgeStyles[error.error_phase] || phaseBadgeStyles.unknown}`}>
                            {error.error_phase}
                          </span>
                        </div>
                        <p className="text-sm text-red-600">{error.error_message}</p>
                        {error.error_stack && (
                          <details className="mt-2">
                            <summary className="text-xs text-red-400 cursor-pointer hover:text-red-600">
                              Stack trace
                            </summary>
                            <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-x-auto max-h-32">
                              {error.error_stack}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Seed Details Drawer Component
function SeedDetailsDrawer({ seed, onClose }: { seed: Seed; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  const { data: seedDetailsRaw, isLoading } = useQuery({
    queryKey: ['admin-seed', seed.id],
    queryFn: () => api.getAdminSeed(seed.id),
  });

  // Cast to our expected type
  const seedDetails = seedDetailsRaw as SeedWithDetails | undefined;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close drawer"
      />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">{seed.name}</h2>
              <p className="text-sm text-gray-500">{seed.timezone}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              &times;
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : seedDetails ? (
              <>
                {/* Status & Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Status</p>
                    <StatusBadge status={seedDetails.status as SeedStatus} />
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Country</p>
                    <p className="font-medium">{seedDetails.country}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Coverage Radius</p>
                    <p className="font-medium">{seedDetails.radius_miles} miles</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500 mb-1">Auto-Assign</p>
                    <p className="font-medium">
                      {seedDetails.auto_assign_enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>

                {/* Last Run Stats */}
                {seedDetails.last_stats && (
                  <div>
                    <h3 className="font-medium mb-3">Last Run Stats</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {/* As-Is */}
                      <div className="bg-blue-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">As-Is</p>
                            <p className="text-lg font-bold text-blue-600">
                              {seedDetails.last_stats.asis?.products || 0}
                            </p>
                          </div>
                          <span className={`text-sm font-medium ${
                            seedDetails.last_stats.asis?.percentage === 100
                              ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {seedDetails.last_stats.asis?.stores || 0} ({seedDetails.last_stats.asis?.percentage || 0}%)
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">stores with data</p>
                      </div>
                      {/* Last Chance */}
                      <div className="bg-orange-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">Last Chance</p>
                            <p className="text-lg font-bold text-orange-600">
                              {seedDetails.last_stats.last_chance?.products || 0}
                            </p>
                          </div>
                          <span className={`text-sm font-medium ${
                            seedDetails.last_stats.last_chance?.percentage === 100
                              ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {seedDetails.last_stats.last_chance?.stores || 0} ({seedDetails.last_stats.last_chance?.percentage || 0}%)
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">stores with data</p>
                      </div>
                      {/* Family Offers */}
                      <div className="bg-purple-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">Family Offers</p>
                            <p className="text-lg font-bold text-purple-600">
                              {seedDetails.last_stats.family_offers?.products || 0}
                            </p>
                          </div>
                          <span className={`text-sm font-medium ${
                            seedDetails.last_stats.family_offers?.percentage === 100
                              ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {seedDetails.last_stats.family_offers?.stores || 0} ({seedDetails.last_stats.family_offers?.percentage || 0}%)
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">stores with data</p>
                      </div>
                      {/* Limited Time */}
                      <div className="bg-green-50 rounded-lg p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-xs text-gray-500">Limited Time</p>
                            <p className="text-lg font-bold text-green-600">
                              {seedDetails.last_stats.limited_time?.products || 0}
                            </p>
                          </div>
                          <span className={`text-sm font-medium ${
                            seedDetails.last_stats.limited_time?.percentage === 100
                              ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {seedDetails.last_stats.limited_time?.stores || 0} ({seedDetails.last_stats.limited_time?.percentage || 0}%)
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">stores with data</p>
                      </div>
                    </div>
                    {/* Locations */}
                    {(seedDetails.last_stats.locations || 0) > 0 && (
                      <div className="mt-3 bg-gray-100 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-gray-600">{seedDetails.last_stats.locations}</p>
                        <p className="text-xs text-gray-500">Store Locations Contributed</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Assigned Stores */}
                <div>
                  <h3 className="font-medium mb-3">
                    Assigned Stores ({seedDetails.assigned_stores?.length || 0})
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {seedDetails.assigned_stores?.map((store) => (
                      <div
                        key={store.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">{store.name}</p>
                          <p className="text-xs text-gray-500">
                            {store.city}, {store.state}
                          </p>
                        </div>
                        <span className="text-xs text-gray-400">{store.id}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Daily Schedule */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium">Daily Schedule</h3>
                  </div>
                  <div className="space-y-2">
                    {seedDetails.schedules?.map((schedule) => (
                      <ScheduleEditRow
                        key={schedule.id}
                        schedule={schedule}
                        seedId={seed.id}
                        onUpdate={() => {
                          queryClient.invalidateQueries({ queryKey: ['admin-seed', seed.id] });
                          queryClient.invalidateQueries({ queryKey: ['admin-schedule-status'] });
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Referral Stats */}
                <div>
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Users size={16} />
                    Referral Stats
                  </h3>
                  <SeedReferralStats seedId={seed.id} />
                </div>

                {/* Recent Runs */}
                <div>
                  <h3 className="font-medium mb-3">Recent Runs</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {seedDetails.recent_runs?.length === 0 ? (
                      <p className="text-sm text-gray-500">No runs yet</p>
                    ) : (
                      seedDetails.recent_runs?.map((run) => (
                        <button
                          type="button"
                          key={run.id}
                          className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => setSelectedRunId(run.id)}
                        >
                          <div>
                            <p className="font-medium text-sm capitalize">
                              {run.run_type || 'Unknown'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(run.started_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  run.status === 'success'
                                    ? 'bg-green-100 text-green-700'
                                    : run.status === 'error'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}
                              >
                                {run.status}
                              </span>
                              {run.products_found !== null && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {run.products_found} products
                                </p>
                              )}
                            </div>
                            <Eye size={14} className="text-gray-400" />
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* Error Details Section */}
                <ErrorDetailsSection seedId={seed.id} />

                {/* Timing Analysis Section */}
                <TimingAnalysisSection seedId={seed.id} />
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Failed to load seed details
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Run Logs Modal */}
      {selectedRunId && (
        <RunLogsModal
          seedId={seed.id}
          runId={selectedRunId}
          onClose={() => setSelectedRunId(null)}
        />
      )}
    </div>
  );
}

// Create Seed Modal Component
function CreateSeedModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: '',
    country: 'US' as 'US' | 'CA',
    timezone: 'America/New_York',
    center_latitude: '',
    center_longitude: '',
    radius_miles: '500',
    auto_assign_enabled: true,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createAdminSeed({
        name: formData.name,
        country: formData.country,
        timezone: formData.timezone,
        center_latitude: formData.center_latitude
          ? parseFloat(formData.center_latitude)
          : undefined,
        center_longitude: formData.center_longitude
          ? parseFloat(formData.center_longitude)
          : undefined,
        radius_miles: parseInt(formData.radius_miles),
        auto_assign_enabled: formData.auto_assign_enabled,
      }),
    onSuccess: () => {
      onCreated();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'America/Toronto', label: 'Toronto' },
    { value: 'America/Vancouver', label: 'Vancouver' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Create New Seed</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="seed-name" className="block text-sm font-medium text-gray-700 mb-1">
              Seed Name
            </label>
            <input
              id="seed-name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Northeast US"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="seed-country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <select
                id="seed-country"
                value={formData.country}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    country: e.target.value as 'US' | 'CA',
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>

            <div>
              <label htmlFor="seed-timezone" className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                id="seed-timezone"
                value={formData.timezone}
                onChange={(e) =>
                  setFormData({ ...formData, timezone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="seed-latitude" className="block text-sm font-medium text-gray-700 mb-1">
                Center Latitude
              </label>
              <input
                id="seed-latitude"
                type="number"
                step="0.000001"
                value={formData.center_latitude}
                onChange={(e) =>
                  setFormData({ ...formData, center_latitude: e.target.value })
                }
                placeholder="e.g., 40.7128"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="seed-longitude" className="block text-sm font-medium text-gray-700 mb-1">
                Center Longitude
              </label>
              <input
                id="seed-longitude"
                type="number"
                step="0.000001"
                value={formData.center_longitude}
                onChange={(e) =>
                  setFormData({ ...formData, center_longitude: e.target.value })
                }
                placeholder="e.g., -74.0060"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label htmlFor="seed-radius" className="block text-sm font-medium text-gray-700 mb-1">
              Coverage Radius (miles)
            </label>
            <input
              id="seed-radius"
              type="number"
              value={formData.radius_miles}
              onChange={(e) =>
                setFormData({ ...formData, radius_miles: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="auto_assign"
              checked={formData.auto_assign_enabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  auto_assign_enabled: e.target.checked,
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="auto_assign" className="text-sm text-gray-700">
              Auto-assign stores within radius
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Seed'}
            </button>
          </div>

          {createMutation.isError && (
            <p className="text-sm text-red-600 text-center">
              Failed to create seed. Please try again.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

// Edit Seed Modal Component
function EditSeedModal({
  seed,
  onClose,
  onUpdated,
}: {
  seed: Seed;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [formData, setFormData] = useState({
    name: seed.name,
    timezone: seed.timezone,
    radius_miles: String(seed.radius_miles),
    auto_assign_enabled: seed.auto_assign_enabled,
  });

  const { addToast } = useToast();

  const updateMutation = useMutation({
    mutationFn: () =>
      api.updateAdminSeed(seed.id, {
        name: formData.name,
        timezone: formData.timezone,
        radius_miles: parseInt(formData.radius_miles),
        auto_assign_enabled: formData.auto_assign_enabled,
      }),
    onSuccess: () => {
      addToast('success', 'Seed updated successfully');
      onUpdated();
    },
    onError: (error: Error) => {
      addToast('error', `Failed to update: ${error.message}`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate();
  };

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time' },
    { value: 'America/Chicago', label: 'Central Time' },
    { value: 'America/Denver', label: 'Mountain Time' },
    { value: 'America/Los_Angeles', label: 'Pacific Time' },
    { value: 'America/Toronto', label: 'Toronto' },
    { value: 'America/Vancouver', label: 'Vancouver' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Europe/Berlin', label: 'Berlin' },
    { value: 'Europe/Stockholm', label: 'Stockholm' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Australia/Sydney', label: 'Sydney' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Seed</h2>
          <span className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
            <img
              src={getCountryFlagUrl(seed.country)}
              alt={seed.country}
              className="w-4 h-3 object-cover rounded-sm"
            />
            {seed.country}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="edit-seed-name" className="block text-sm font-medium text-gray-700 mb-1">
              Seed Name
            </label>
            <input
              id="edit-seed-name"
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label htmlFor="edit-seed-timezone" className="block text-sm font-medium text-gray-700 mb-1">
              Timezone
            </label>
            <select
              id="edit-seed-timezone"
              value={formData.timezone}
              onChange={(e) =>
                setFormData({ ...formData, timezone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="edit-seed-radius" className="block text-sm font-medium text-gray-700 mb-1">
              Coverage Radius (miles)
            </label>
            <input
              id="edit-seed-radius"
              type="number"
              value={formData.radius_miles}
              onChange={(e) =>
                setFormData({ ...formData, radius_miles: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="edit_auto_assign"
              checked={formData.auto_assign_enabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  auto_assign_enabled: e.target.checked,
                })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="edit_auto_assign" className="text-sm text-gray-700">
              Auto-assign stores within radius
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
